import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, ChevronDown, ChevronUp } from "lucide-react";
import { changelogFor } from "@/lib/changelog";

const STORAGE_KEY = "cc_whatsnew_seen";

export function WhatsNew({ role }) {
  const navigate = useNavigate();
  const items = changelogFor(role);
  const latestId = items[0]?.id;
  const seen = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const hasNew = latestId && seen !== latestId;
  const [open, setOpen] = useState(false); // stays collapsed until the user clicks it
  const [dismissed, setDismissed] = useState(false);

  if (!items.length || dismissed) return null;

  const markSeen = () => { localStorage.setItem(STORAGE_KEY, latestId); };
  const close = () => { markSeen(); setDismissed(true); };
  const toggle = () => { if (!open) markSeen(); setOpen((o) => !o); };

  return (
    <div className="mb-6 rounded-xl border border-cc-blue/20 bg-gradient-to-br from-cc-blue/[0.06] to-transparent" data-testid="whats-new">
      <button onClick={toggle} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="whats-new-toggle">
        <span className="flex items-center gap-2.5">
          <span className="grid place-items-center w-7 h-7 rounded-lg bg-cc-blue/10"><Sparkles className="w-4 h-4 text-cc-blue" /></span>
          <span className="font-semibold text-zinc-900">What's new</span>
          {hasNew && <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-cc-blue px-2 py-0.5 rounded-full" data-testid="whats-new-badge">New</span>}
        </span>
        <span className="flex items-center gap-1">
          {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          <span onClick={(e) => { e.stopPropagation(); close(); }} data-testid="whats-new-dismiss" className="ml-1 p-1 text-zinc-400 hover:text-zinc-700 rounded"><X className="w-4 h-4" /></span>
        </span>
      </button>
      {open && (
        <ul className="px-5 pb-4 space-y-3">
          {items.slice(0, 6).map((c) => (
            <li key={c.id} className="flex gap-3" data-testid={`whats-new-item-${c.id}`}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cc-blue shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-900">{c.title} <span className="text-xs font-normal text-zinc-400 ml-1">{c.date}</span></p>
                <p className="text-sm text-zinc-600">{c.desc}</p>
              </div>
            </li>
          ))}
          <li className="pt-1">
            <button onClick={() => navigate("/help")} className="text-sm font-medium text-cc-blue hover:underline" data-testid="whats-new-help-link">See how it works in the Help Center →</button>
          </li>
        </ul>
      )}
    </div>
  );
}
